import { fetchWithHeaders } from "./fetchWithHeaders";

export interface Thread {
  id: string;
  title: string;
  user_id: string;
  created_at: string;
  updated_at?: string;
}

export interface ChatMessage {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  user_id: string;
  created_at: string;
  updated_at?: string;
  feedback?: string;
  reasoning_steps?: Array<{
    content: string;
    timestamp: string;
    tool_name?: string;
    tool_args?: any;
    tool_result?: string;
    tool_result_preview?: string;
  }>;
  references?: Array<{
    reference_id: string;
    reference_type: 'table' | 'document' | 'image';
    title: string;
    file_id?: number;
    page_number?: number;
    data?: {
      columns?: string[];
      rows?: Record<string, any>[];
      metadata?: {
        sql?: string;
        row_count?: number;
        question?: string;
        timestamp?: number;
      };
    };
    timestamp: string;
  }>;
}

export interface FeedbackOptions {
  factsWithoutNarrative: boolean;
  weakCommercialLink: boolean;
  risksNotAcknowledged: boolean;
  incorrectSource: boolean;
  incorrectAnswer: boolean;
}

export interface FeedbackData {
  messageId: string;
  feedbackType: 'up' | 'down';
  feedbackText?: string;
  feedbackOptions?: FeedbackOptions;
}

export interface FileUploadParams {
  negotiationId: number;
  indexId: string;
  thread_id: any // Add current thread ID

}

/* --------------------- */
/* ✅ Runtime configuration */
/* --------------------- */
let apiBase: string = import.meta.env.VITE_API_BASE_URL ?? '';

export class ChatAPI {
  /** Called by your web component/store when Angular passes JWT */
  /** Called by your web component/store when Angular passes endpoint */
  static setBaseUrl(base: string) {
    apiBase = base;
  }

  /* --------------------- */
  /* 🧩 Core API methods   */
  /* --------------------- */

  static async listThreads(): Promise<Thread[]> {
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/thread/`, {
      method: 'GET',
    });
       if (!response.ok) {
      throw new Error(`Failed to list threads: ${response.status}`);
    }
    return response.json();
  }

  static async getThread(threadId: string): Promise<Thread> {
    const response:any = await fetchWithHeaders(`${apiBase}api/v1/thread/${threadId}`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`Failed to get thread: ${response.status}`);
    }
    return response.json();
  }

  static async createThread(title: string): Promise<Thread> {
    const response:any = await fetchWithHeaders(`${apiBase}api/v1/thread/`, {
      method: 'POST',
      body: JSON.stringify({
        title
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create thread: ${response.status}`);
    }
    return response.json();
  }

  static async updateThread(threadId: string, title: string): Promise<Thread> {
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/thread/${threadId}`, {
      method: 'PUT',
      body: JSON.stringify({
        title
      }),
    });
    if (!response.ok) throw new Error(`Failed to update thread: ${response.status}`);
    return response.json();
  }

  static async deleteThread(threadId: string): Promise<void> {
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/thread/${threadId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error(`Failed to delete thread: ${response.status}`);
  }

  static async getMessages(threadId: string): Promise<ChatMessage[]> {
    const response:any = await fetchWithHeaders(`${apiBase}api/v1/thread/${threadId}/messages`,{
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`Failed to get messages: ${response.status}`);
    }
    return response.json();
  }

  static async sendMessage(
    text: string,
    threadId: string,
    negotiationId?: number,
    agentType?: string,
    supplierId?: number,
    tenderId?: number
  ): Promise<ReadableStream<Uint8Array> | null> {
    const payload: Record<string, unknown> = {
      message: text,
      thread_id: threadId,
      ...(negotiationId !== undefined ? { negotiation_id: negotiationId } : {}),
      ...(agentType ? { agent_type: agentType } : {}),
      ...(supplierId !== undefined ? { supplier_id: supplierId } : {}),
      ...(tenderId !== undefined ? { tender_id: tenderId } : {}),
    };
    
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/chat/stream`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errText}`);
    }

    return response.body;
  }

  static async submitFeedback(feedbackData: FeedbackData, threadId: string): Promise<void> {
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/chat/feedback`, {
      method: 'POST',
      body: JSON.stringify({
        message_id: feedbackData.messageId,
        feedback_type: feedbackData.feedbackType,
        feedback_text: feedbackData.feedbackText,
        feedback_options: feedbackData.feedbackOptions,
        thread_id: threadId,
      }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    return response.json();
  }

  static async uploadFile(file: File, params: FileUploadParams): Promise<ReadableStream<Uint8Array> | null> {
    const formData = new FormData();
    formData.append('file', file);

    const queryParams = new URLSearchParams({
      negotiation_id: String(params.negotiationId),
      index_id: params.indexId,
      thread_id: params.thread_id

    });
    
    const response: any = await fetchWithHeaders(`${apiBase}api/v1/file/upload_file_stream?${queryParams.toString()}`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed ${response.status}: ${text}`);
    }
    return response.body;
  }

  static async getDocumentReference(fileId: number): Promise<Blob> {
    const response: any = await fetchWithHeaders(
      `${apiBase}api/v1/file/${fileId}/reference`,
      { method: 'GET' }
    );
    if (!response.ok) {
      throw new Error(`Failed to load document (${response.status})`);
    }
    return response.blob();
  }
}
